// ============================================
// NRS.08 — Session State & Handoff Types
// ============================================
// Schema chain: SafetyDataProfile + DataCompletenessFlags → NurseCompletionEvent → PRO.01
// Contract: Atomic persist, canonical path sessions/{sessionId}/safetyProfile (FD-NRS-1)
// ============================================

import type { DataCompletenessFlags, ReproductiveStatus } from './triageSession';

// ── Safety Data Profile ──

export type SafetyDataSource = 'modal' | 'chat' | 'triage' | 'conservative_default';

export interface SourceAttribution {
  field: string;
  source: SafetyDataSource;
  turn?: number;
  timestamp: Date;
}

export interface SafetyDataProfile {
  // ── PRO.01 contract fields (LOCKED) ──
  /** Patient sex from triage */
  sex?: 'male' | 'female';
  /** Patient age from triage */
  age?: number;
  /** Medications collected via modal (NRS.05) */
  modalMedications: string[];
  /** Medications mentioned in chat (NRS.06) */
  chatMedications: string[];
  /** Allergies collected via modal */
  modalAllergies: string[];
  /** Allergies mentioned in chat */
  chatAllergies: string[];
  /** Conditions collected via modal */
  modalConditions: string[];
  /** Conditions mentioned in chat */
  chatConditions: string[];
  /** Pregnancy status from modal */
  isPregnant?: boolean;
  isBreastfeeding?: boolean;
  // ── Completeness flags (flattened for PRO.01 contract) ──
  medicationsProvided: boolean;
  allergiesProvided: boolean;
  conditionsProvided: boolean;
  sexProvided: boolean;
  ageProvided: boolean;
  pregnancyStatusProvided: boolean;
  /** ISO 8601 timestamp of last profile update */
  lastUpdated: string;
  // ── Nurse-internal fields (PRO.01 ignores these) ──
  isTryingToConceive?: boolean;
  /** Reproductive status from triage */
  reproductiveStatus: ReproductiveStatus;
  /** Source attribution for each field */
  sourceLog: SourceAttribution[];
}

// ── Persisted Session State ──

export interface PersistedSessionState {
  /** Existing fields from sessionState.ts */
  modifiers: Record<string, unknown>;
  lateReveals: Array<{ turn: number; modifier: string; category: 'safety_critical' | 'preference' }>;
  phase: string;
  turnNumber: number;
  phase1Complete: boolean;
  protocolPresented: boolean;
  /** NEW: Safety data profile with source attribution */
  safetyDataProfile: SafetyDataProfile;
  /** NEW: Data completeness flags */
  dataCompleteness: DataCompletenessFlags;
  /** NEW: Last active timestamp for gap detection */
  lastActiveAt: Date;
  /** Firestore server timestamp */
  updatedAt: Date;
}

// ── Events ──

export interface NurseCompletionEvent {
  eventId: string;
  sessionId: string;
  patientId: string;
  safetyDataProfileComplete: boolean;
  triggerSource: 'build_protocol_marker' | 'manual_trigger';
  dataCompletenessFlags: DataCompletenessFlags;
  /** ISO 8601 timestamp — PRO.01 reads this field */
  completedAt: string;
  turnNumber: number;
}

export interface SessionResumeEvent {
  eventId: string;
  sessionId: string;
  patientId: string;
  /** PRO.11-B12 LOCKED contract requires protocolId */
  protocolId: string | null;
  resumeTrigger: 'page_load' | 'auth_restore' | 'tab_focus' | 'explicit_return';
  gapDurationMs: number;
  /** ISO 8601 timestamp */
  previousLastActiveAt: string;
  /** ISO 8601 timestamp */
  resumedAt: string;
}

// ── Gap Detection ──

export interface GapDetectionConfig {
  /** Minimum gap in ms to trigger SessionResumeEvent (5 minutes) */
  gapThresholdMs: number;
  /** How to detect concurrent tabs */
  concurrentTabDetection: 'broadcast_channel' | 'localstorage_heartbeat';
}

export const DEFAULT_GAP_DETECTION: GapDetectionConfig = {
  gapThresholdMs: 5 * 60 * 1000,
  concurrentTabDetection: 'broadcast_channel',
};

// ── Network Retry ──

export interface RetryConfig {
  maxRetries: number;
  initialBackoffMs: number;
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialBackoffMs: 1000,
  backoffMultiplier: 2,
};

// ── Canonical Paths ──

export const SESSION_SAFETY_PROFILE_PATH = 'sessions/{sessionId}/safetyProfile';
export const NURSE_COMPLETION_EVENT_PATH = 'sessions/{sessionId}/events/nurse_completion';
export const SESSION_RESUME_EVENT_PATH = 'sessions/{sessionId}/events/resume_{eventId}';

// ── Metrics ──

export interface SessionHandoffMetrics {
  totalCalls: number;
  successRate: number;
  avgLatencyMs: number;
  errorsByType: Record<string, number>;
  accuracyOnGoldStandard: number;
}
